import React, { useState } from 'react';
import {
  format,
  addMonths,
  subMonths,
  isSameDay,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  CheckCircle2,
  AlertCircle,
  Clock,
  Check,
  Edit2,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import {
  getCalendarGridDays,
  getBillsForDay,
  getBillDueStatus,
  BILL_STATUS_META,
} from 'shared/utils/billsCalendarUtils';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

export default function BillsCalendarView({
  bills = [],
  currentMonth = new Date(),
  onMonthChange,
  onOpenAddDialog,
  onOpenEditDialog,
  onMarkPaid,
  onDeleteBill,
  formatCurrency,
  theme,
  isDark,
}) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayBills, setSelectedDayBills] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);

  const days = getCalendarGridDays(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => {
    if (onMonthChange) onMonthChange(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    if (onMonthChange) onMonthChange(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    if (onMonthChange) onMonthChange(new Date());
  };

  const handleDayClick = (day) => {
    const dayBills = getBillsForDay(bills, day.date);
    setSelectedDay(day.date);
    setSelectedDayBills(dayBills);
    setShowDayModal(true);
  };

  return (
    <div
      className="rounded-2xl p-5 md:p-6 shadow-sm border transition-all"
      style={{
        backgroundColor: theme.cardBackground,
        borderColor: isDark ? '#3A3A3C' : '#E5E5EA',
      }}
    >
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${theme.accent}15` }}
          >
            <CalendarIcon className="w-5 h-5" style={{ color: theme.accent }} />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight" style={{ color: theme.text }}>
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <p className="text-xs" style={{ color: theme.textSecondary }}>
              Click any day to view due bills or schedule a new one
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
            className="text-xs h-8 px-3 rounded-lg"
            style={{
              borderColor: isDark ? '#48484A' : '#D1D5DB',
              color: theme.text,
            }}
          >
            Today
          </Button>

          <div
            className="flex items-center rounded-lg border p-0.5"
            style={{ borderColor: isDark ? '#48484A' : '#E5E5EA' }}
          >
            <button
              onClick={handlePrevMonth}
              className="p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title="Previous Month"
              style={{ color: theme.text }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title="Next Month"
              style={{ color: theme.text }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Weekday Column Headers */}
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 text-center">
        {weekDays.map((wd, i) => (
          <div
            key={wd}
            className="py-1.5 text-xs font-semibold uppercase tracking-wider"
            style={{
              color: i === 0 || i === 6 ? (isDark ? '#E57373' : '#DC2626') : theme.textSecondary,
            }}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* 7-Column Monthly Days Grid */}
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {days.map((day) => {
          const dayBills = getBillsForDay(bills, day.date);
          const hasBills = dayBills.length > 0;
          const hasUnpaid = dayBills.some((b) => !b.isPaid);
          const hasOverdue = dayBills.some(
            (b) => !b.isPaid && getBillDueStatus(b, new Date()) === 'overdue'
          );

          return (
            <div
              key={day.dateString}
              onClick={() => handleDayClick(day)}
              className={`min-h-[86px] md:min-h-[105px] p-1.5 md:p-2 rounded-xl border flex flex-col justify-between cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md ${
                !day.isCurrentMonth ? 'opacity-40 bg-transparent' : ''
              }`}
              style={{
                backgroundColor: day.isToday
                  ? isDark
                    ? '#2C2C2E'
                    : '#FFF5F5'
                  : isDark
                  ? '#1E1E20'
                  : '#FAFAFA',
                borderColor: day.isToday
                  ? theme.accent
                  : hasOverdue
                  ? '#EF4444'
                  : isDark
                  ? '#2C2C2E'
                  : '#E5E5EA',
                borderWidth: day.isToday ? '2px' : '1px',
              }}
            >
              {/* Day Header */}
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                    day.isToday ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: day.isToday ? theme.accent : 'transparent',
                    color: day.isToday ? '#FFFFFF' : theme.text,
                  }}
                >
                  {day.dayNumber}
                </span>

                {hasBills && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: hasOverdue
                        ? '#EF444420'
                        : hasUnpaid
                        ? '#F59E0B20'
                        : '#10B98120',
                      color: hasOverdue
                        ? '#EF4444'
                        : hasUnpaid
                        ? '#D97706'
                        : '#10B981',
                    }}
                  >
                    {dayBills.length}
                  </span>
                )}
              </div>

              {/* Bills Mini Chips */}
              <div className="flex flex-col gap-1 mt-1 overflow-hidden">
                {dayBills.slice(0, 2).map((bill) => {
                  const status = getBillDueStatus(bill, new Date());
                  const meta = BILL_STATUS_META[status] || BILL_STATUS_META.upcoming;

                  return (
                    <div
                      key={bill.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDayClick(day);
                      }}
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium truncate flex items-center justify-between border"
                      style={{
                        backgroundColor: meta.bg,
                        color: meta.textDark,
                        borderColor: `${meta.color}40`,
                      }}
                      title={`${bill.name}: ${formatCurrency(bill.amount)} (${meta.label})`}
                    >
                      <span className="truncate max-w-[65%]">{bill.name}</span>
                      <span className="font-bold">{formatCurrency(bill.amount)}</span>
                    </div>
                  );
                })}

                {dayBills.length > 2 && (
                  <span
                    className="text-[9px] font-semibold text-center mt-0.5"
                    style={{ color: theme.textSecondary }}
                  >
                    +{dayBills.length - 2} more
                  </span>
                )}
              </div>

              {/* Day Bottom Indicator */}
              <div className="h-1 flex items-center justify-end">
                {hasBills && (
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: hasOverdue
                        ? '#EF4444'
                        : hasUnpaid
                        ? '#F59E0B'
                        : '#10B981',
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Day Bills Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{ backgroundColor: theme.cardBackground, color: theme.text }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-lg">
              <span style={{ color: theme.text }}>
                {selectedDay ? format(selectedDay, 'EEEE, MMMM d, yyyy') : 'Day Bills'}
              </span>
              {selectedDayBills.length > 0 && (
                <span
                  className="text-xs px-2.5 py-1 rounded-full font-semibold"
                  style={{
                    backgroundColor: `${theme.accent}20`,
                    color: theme.accent,
                  }}
                >
                  {selectedDayBills.length} Bill{selectedDayBills.length !== 1 ? 's' : ''}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {selectedDayBills.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium" style={{ color: theme.text }}>
                  No bills scheduled for this day
                </p>
                <p className="text-xs mt-1 mb-4" style={{ color: theme.textSecondary }}>
                  Keep track of recurring utilities, credit cards, or rent.
                </p>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowDayModal(false);
                    if (onOpenAddDialog) {
                      onOpenAddDialog(selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null);
                    }
                  }}
                  style={{ backgroundColor: theme.accent, color: '#FFFFFF' }}
                  className="rounded-lg text-xs"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Bill for this Date
                </Button>
              </div>
            ) : (
              selectedDayBills.map((bill) => {
                const status = getBillDueStatus(bill, new Date());
                const meta = BILL_STATUS_META[status] || BILL_STATUS_META.upcoming;

                return (
                  <div
                    key={bill.id}
                    className="p-3.5 rounded-xl border flex flex-col gap-2.5"
                    style={{
                      backgroundColor: isDark ? '#252528' : '#F9FAFB',
                      borderColor: isDark ? '#3A3A3C' : '#E5E5EA',
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-sm" style={{ color: theme.text }}>
                          {bill.name}
                        </h4>
                        <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                          {bill.repeat && bill.repeat !== 'none'
                            ? `Repeats ${bill.repeat}`
                            : 'One-time bill'}{' '}
                          {bill.account ? `• Debited from ${bill.account}` : ''}
                        </p>
                      </div>

                      <div className="text-right">
                        <span className="font-bold text-base" style={{ color: theme.text }}>
                          {formatCurrency(bill.amount)}
                        </span>
                        <div>
                          <span
                            className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mt-1"
                            style={{
                              backgroundColor: meta.bg,
                              color: meta.textDark,
                            }}
                          >
                            {meta.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-black/5 dark:border-white/5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setShowDayModal(false);
                            if (onOpenEditDialog) onOpenEditDialog(bill);
                          }}
                          className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 text-xs font-medium flex items-center gap-1"
                          style={{ color: theme.textSecondary }}
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowDayModal(false);
                            if (onDeleteBill) onDeleteBill(bill);
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-xs font-medium flex items-center gap-1 text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>

                      {!bill.isPaid && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowDayModal(false);
                            if (onMarkPaid) onMarkPaid(bill);
                          }}
                          className="h-7 text-xs px-3 rounded-lg flex items-center gap-1"
                          style={{ backgroundColor: '#10B981', color: '#FFFFFF' }}
                        >
                          <Check className="w-3.5 h-3.5" /> Mark Paid
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="flex sm:justify-between items-center gap-2 pt-2 border-t">
            {selectedDayBills.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDayModal(false);
                  if (onOpenAddDialog) {
                    onOpenAddDialog(selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null);
                  }
                }}
                className="text-xs rounded-lg"
                style={{ color: theme.text }}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Another Bill
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDayModal(false)}
              className="text-xs rounded-lg ml-auto"
              style={{ color: theme.text }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
